/**
 * IncomingOrdersTable (index.jsx)
 *
 * \src\components\IncomingOrders\IncomingOrdersTable\index.jsx
 */

import {
  forwardRef,
  useCallback,
  useEffect,
  useMemo,
  useState,
  useImperativeHandle,
} from "react";
import { FormattedMessage } from "react-intl";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import ArrowDropUpIcon from "@mui/icons-material/ArrowDropUp";
import KeyboardArrowLeftIcon from '@mui/icons-material/KeyboardArrowLeft';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import MoreVertIcon from "@mui/icons-material/MoreVert";
import {
  Box,
  Checkbox,
  CircularProgress,
  Divider,
  FormControl,
  IconButton,
  ListItemText,
  ListSubheader,
  Menu,
  MenuItem,
  OutlinedInput,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";

import InfiniteScrollSentinel from "./InfiniteScrollSentinel";
import OrderTableItem from "./OrderTableItem";

const PAGE_SIZE_OPTIONS = [10, 25, 50, 75, 100, 200];

const IncomingOrdersTable = forwardRef((props, ref) => {
  const {
    ascending = false,
    columnConfig,
    displayMode = "pagination",
    headers = [],
    loading = false,
    onLoadMore,
    onPageChange,
    onPageSizeChange,
    onShowOrderDetails,
    onSortChange,
    orders = [],
    page = 0,
    sortField,
    totalRowCount = 0,
  } = props;

  const STORAGE_KEY = "incomingOrders.visibleColumns";

  const [columnMenuContext, setColumnMenuContext] = useState(null);
  const [columnPanelAnchor, setColumnPanelAnchor] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [pageSize, setPageSize] = useState(PAGE_SIZE_OPTIONS[0] || 10);

  const [visibleColumns, setVisibleColumns] = useState(() => {
    if (typeof window === "undefined") {
      return Object.fromEntries(headers.map(h => [h, true]));
    }

    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored) {
        return JSON.parse(stored);
      }
    } catch (err) {
      console.warn("Failed to parse stored column settings", err);
    }

    return Object.fromEntries(headers.map(h => [h, true]));
  });

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(visibleColumns));
    } catch (err) {
      console.warn("Failed to persist column settings", err);
    }
  }, [visibleColumns]);

  const maxPage = Math.max(0, Math.ceil(totalRowCount / pageSize) - 1);

  const showHeader = (field) => visibleColumns[field] !== false;

  const visibleHeaderDefs = useMemo(
    () => headers.filter(h => showHeader(h)),
    [headers, visibleColumns]
  );

  const allHeadersForPanel = useMemo(
    () => [
      ...headers,
      // Exempel på extra kolumn(er) i framtiden kan läggas här
    ],
    [headers]
  );



  const openColumnMenu = (event, columnType) => {
    event.stopPropagation();

    const { required = false, sortField } = columnConfig[columnType];

    setColumnMenuContext({ anchorEl: event.currentTarget, columnType, required, sortField });
  };

  const closeColumnMenu = () => {
    setColumnMenuContext(null);
  };

  const openColumnPanel = (event) => {
    if (event) {
      setColumnPanelAnchor(event.currentTarget);
    } else {
      setColumnPanelAnchor((prev) => prev || document.body);
    }
  };

  const closeColumnPanel = () => setColumnPanelAnchor(null);

  const handleSort = (field, direction) => {
    if (field && onSortChange) {
      onSortChange(field, direction);
    }
    closeColumnMenu();
  };

  const handleSortAsc = () => handleSort(columnMenuContext.sortField, true);

  const handleSortDesc = () => handleSort(columnMenuContext.sortField, false);

  const handleHeaderClick = (field) => handleSort(field, sortField === field ? !ascending : true);

  const handleHideColumn = () => {
    if (!columnMenuContext) return;
    const { columnType } = columnMenuContext;
    setVisibleColumns((prev) => ({
      ...prev,
      [columnType]: false,
    }));
    closeColumnMenu();
  };

  const handleToggleColumn = useCallback((columnType) => {
    setVisibleColumns((prev) => ({
      ...prev,
      [columnType]: !prev[columnType],
    }));
  }, []);

  const handleChangePage = (direction) => onPageChange?.(
    (direction === "next")
      ? Math.min(page + 1, maxPage)
      : Math.max(page - 1, 0)
  );



  useEffect(() => {
    setVisibleColumns((prev) => {
      const next = { ...prev };
      headers.forEach((h) => {
        if (next[h] === undefined) {
          next[h] = true;
        }
      });
      return next;
    });
  }, [headers]);

  useEffect(() => {
    setIsLoading(false);
  }, [orders]);



  // Expose openColumnPanel to parent via forwarded ref
  useImperativeHandle(ref, () => ({
    openColumnPanel: (event) => openColumnPanel(event),
  }));



  const PageNavigation = () => {
    const startOffset = Math.min(page * pageSize + 1, totalRowCount);
    const endOffset = Math.min((page + 1) * pageSize, totalRowCount);

    return (
      <>
        <Typography variant="body2" sx={{ pr: 0.5 }}>
          {startOffset}–{endOffset} av {totalRowCount}
        </Typography>
        <Box sx={{ display: "flex", gap: 0 }}>
          <IconButton
            size="small"
            aria-label="previous page"
            sx={{ textTransform: "none" }}
            disabled={page === 0}
            onClick={() => handleChangePage("prev")}
          >
            <KeyboardArrowLeftIcon />
          </IconButton>
          <IconButton
            size="small"
            aria-label="next page"
            sx={{ textTransform: "none" }}
            disabled={page === maxPage}
            onClick={() => handleChangePage("next")}
          >
            <KeyboardArrowRightIcon />
          </IconButton>
        </Box>
      </>
    );
  };

  const PageSizeSelector = () => {
    return (
      <>
        <Typography>Rader per sida</Typography>
        <FormControl size="small" variant="outlined" sx={{ px: 0.25, pr: 1 }}>
          <Select
            value={pageSize}
            onChange={(e) => {
              const size = Number(e.target.value);
              setPageSize(size);
              onPageSizeChange?.(size);
            }}
            input={
              <OutlinedInput
                sx={{
                  "& .MuiOutlinedInput-notchedOutline": {
                    borderWidth: "0 !important",
                  },
                  "&:hover .MuiOutlinedInput-notchedOutline, &.Mui-focused .MuiOutlinedInput-notchedOutline": {
                    borderWidth: "1px !important",
                  },
                }}
              />
            }
          >
            {PAGE_SIZE_OPTIONS.map((size) => (
              <MenuItem key={size} value={size}>
                {size}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </>
    );
  };



  if (loading) {
    return (
      <Box sx={{ textAlign: "center", py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!orders.length) {
    return (
      <Box sx={{ textAlign: "center", py: 3 }}>
        <Typography color="error">❗ Inga ordrar hittades</Typography>
      </Box>
    );
  }

  return (
    <Box>
      <Paper elevation={1} sx={{ borderRadius: 2, overflow: "hidden" }}>
        <TableContainer>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                {visibleHeaderDefs.map((columnType) => {
                  const h = columnConfig[columnType];
                  const isSortable = Boolean(h.sortField);
                  return (
                    <TableCell
                      key={columnType}
                      sx={{
                        backgroundColor: "#f5f5f5",
                        fontWeight: 600,
                        whiteSpace: "nowrap",
                        padding: 0,
                        '&:hover .colMenuButton, &:focus-within .colMenuButton': {
                          opacity: 1,
                          pointerEvents: 'auto',
                        },
                      }}
                      align={h.align || "left"}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent:
                            (h.align || "left") === "right"
                              ? "flex-end"
                              : "flex-start",
                          gap: 0.5,
                          pl: 2,
                          pr: 0,
                          py: 1,
                          borderRight: '1px solid rgba(0,0,0,0.08)',
                        }}
                      >
                        <Box
                          onClick={() => isSortable && handleHeaderClick(h.sortField)}
                          sx={{
                            display: "inline-flex",
                            alignItems: "center",
                            ...(isSortable && { cursor: "pointer" }),
                            userSelect: "none",
                            gap: 0.2,
                          }}
                        >
                          <span>{h.label}</span>
                          {sortField === h.sortField && (
                            ascending
                              ? <ArrowDropUpIcon fontSize="small" />
                              : <ArrowDropDownIcon fontSize="small" />
                          )}
                        </Box>
                        <IconButton
                          size="small"
                          className="colMenuButton"
                          onClick={(e) => openColumnMenu(e, columnType)}
                          sx={{
                            ml: -1,
                            opacity: 0,
                            pointerEvents: 'none',
                            transition: 'opacity 140ms ease',
                          }}
                        >
                          <MoreVertIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    </TableCell>
                  )
                })}
              </TableRow>
            </TableHead>

            <TableBody>
              {orders.map(order => (
                <OrderTableItem
                  key={order.orderId}
                  order={order}
                  onShowOrderDetails={onShowOrderDetails}
                  visibleFields={visibleColumns}
                />
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {displayMode === "pagination" && (
        <Box
          sx={{
            display: "flex",
            justifyContent: "flex-end",
            alignItems: "center",
            mt: 1,
          }}
        >
          <PageSizeSelector />
          <PageNavigation />
        </Box>
      )}

      {displayMode === "infinite" && (
        <InfiniteScrollSentinel
          isActive={orders.length < totalRowCount}
          isLoading={loading || isLoading}
          loadingText={<FormattedMessage id="incomingOrders.sentinel.loadingText" />}
          scrollText={<FormattedMessage id="incomingOrders.sentinel.scrollText" />}
          onVisible={() => {
            if (typeof onLoadMore === "function") {
              setIsLoading(true);
              onLoadMore();
            }
          }}
        />
      )}

      <Menu /* ColumnMenu */
        anchorEl={columnMenuContext?.anchorEl}
        open={Boolean(columnMenuContext?.anchorEl)}
        onClose={closeColumnMenu}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <MenuItem
          onClick={handleSortAsc}
          disabled={!Boolean(columnMenuContext?.sortField)}
        >
          Sortera stigande
        </MenuItem>
        <MenuItem
          onClick={handleSortDesc}
          disabled={!Boolean(columnMenuContext?.sortField)}
        >
          Sortera fallande
        </MenuItem>
        <Divider />
        <MenuItem
          onClick={handleHideColumn}
          disabled={Boolean(columnMenuContext?.required)}
        >
          Dölj kolumn
        </MenuItem>
        <Divider />
        <MenuItem
          onClick={(e) => {
            openColumnPanel(e);
            closeColumnMenu();
          }}
        >
          Visa/dölj flera kolumner…
        </MenuItem>
      </Menu>

      <Menu /* ColumnPanel */
        keepMounted
        anchorEl={columnPanelAnchor}
        open={Boolean(columnPanelAnchor)}
        onClose={closeColumnPanel}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
        transformOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <ListSubheader
          disableSticky
          sx={{ px: 2, pt: 1.5, pb: 1 }}
        >
          <Typography
            variant="subtitle2"
            sx={{ fontWeight: 600, color: "text.primary" }}
          >
            Visa kolumner
          </Typography>
        </ListSubheader>

        {allHeadersForPanel.map((columnType) => {
          const { label, required } = columnConfig[columnType];
          return (
            <MenuItem
              key={columnType}
              dense
              disabled={required}
              onClick={() => handleToggleColumn(columnType)}
              sx={{ px: 1, py: 0 }}
            >
              <Checkbox
                size="small"
                checked={visibleColumns[columnType] !== false}
              />
              <ListItemText
                primary={label}
                sx={{ mr: 1 }}
              />
            </MenuItem>
          );
        })}
      </Menu>
    </Box>
  );
});

IncomingOrdersTable.displayName = "IncomingOrdersTable";

export default IncomingOrdersTable;
